import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase, safeChannel } from "./supabase";

// useKidVisiblePantryItems — pantry items the parent has flagged as
// requestable by the kid. Migration 0244 added kid_visible /
// kid_label / kid_kind to pantry_items; this hook is the kid-side
// read path, family-scoped via the existing pantry_items RLS.
//
// Returns rows bucketed by kid_kind ({ meal, snack, dessert }) so
// the FoodExplorer can drop them into the matching segment without
// re-bucketing in the view.
//
// Realtime: subscribes to pantry_items changes scoped to the kid's
// channel so a parent flipping kid_visible on their phone shows up
// on the kid's iPad within a second.

const SELECT_COLS = [
  "id", "user_id", "name", "brand", "emoji", "canonical_id",
  "barcode_upc", "amount", "unit", "tile_id", "location",
  "kid_visible", "kid_label", "kid_kind",
  "updated_at",
].join(", ");

export function useKidVisiblePantryItems(kidUserId) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const chRef = useRef(null);

  const load = useCallback(async () => {
    if (!kidUserId) { setRows([]); setLoading(false); return; }
    setLoading(true);
    const { data, error: err } = await supabase
      .from("pantry_items")
      .select(SELECT_COLS)
      .eq("kid_visible", true)
      .order("kid_kind", { ascending: true })
      .order("updated_at", { ascending: false });
    if (err) {
      console.warn("[kid-visible-pantry] load:", err.message);
      setError(err);
    }
    const pantryRows = data || [];

    // Inline icon resolution: rather than depending on the global
    // dbMap (24h localStorage cache + provider context), we do a
    // single small SELECT against ingredient_info for the canonicals
    // we just loaded and stamp `iconUrl` directly onto each row.
    // Bypass means: when a parent triggers generate-canonical-emoji,
    // the next time the kid app loads pantry items, the icon shows
    // — no cache invalidation dance required.
    const canonicalIds = Array.from(new Set(
      pantryRows.map((r) => r.canonical_id).filter(Boolean),
    ));
    let iconByCanonical = new Map();
    if (canonicalIds.length > 0) {
      const { data: infoRows, error: infoErr } = await supabase
        .from("ingredient_info")
        .select("ingredient_id, info")
        .in("ingredient_id", canonicalIds);
      if (infoErr) {
        console.warn("[kid-visible-pantry] info join failed:", infoErr.message);
      } else {
        for (const r of (infoRows || [])) {
          const url = r?.info?.imageUrl;
          if (url) iconByCanonical.set(r.ingredient_id, url);
        }
      }
    }
    const enriched = pantryRows.map((r) => ({
      ...r,
      icon_url: iconByCanonical.get(r.canonical_id) || null,
    }));
    setRows(enriched);
    setLoading(false);
  }, [kidUserId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!kidUserId) return;
    const ch = safeChannel(`rt:pantry_items_kid:${kidUserId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "pantry_items" },
        () => load(),
      )
      .subscribe();
    chRef.current = ch;
    return () => {
      if (chRef.current) {
        supabase.removeChannel(chRef.current);
        chRef.current = null;
      }
    };
  }, [kidUserId, load]);

  // Bucket by kid_kind. Display name prefers kid_label nickname
  // when set, falls back to brand + name (e.g. "Slim Jim Ground
  // Chicken") and finally to plain name.
  const decorate = (r) => ({
    ...r,
    displayName: (r.kid_label && r.kid_label.trim())
      || (r.brand ? `${r.brand} ${r.name}`.trim() : r.name)
      || "Item",
  });

  const buckets = useMemo(() => {
    const meal = [], snack = [], dessert = [];
    for (const r of rows) {
      const decorated = decorate(r);
      const k = r.kid_kind || "snack";
      if      (k === "dessert") dessert.push(decorated);
      else if (k === "meal")    meal.push(decorated);
      else                       snack.push(decorated);
    }
    return { meal, snack, dessert };
  }, [rows]);

  return { rows, buckets, loading, error, refresh: load };
}
